import { onMounted, onUnmounted, watch } from 'vue'

/**
 * The event `BaseRpcClient.handlePasswordChangeRequired()` raises when the
 * server refuses a call with `error.data.reason = 'password_change_required'`.
 */
export const PASSWORD_CHANGE_REQUIRED_EVENT = 'password-change-required'

/**
 * Send a gated session to the forced-password-change screen (PCI DSS 8.3.5,
 * #1198) when the RPC client reports that refusal.
 *
 * Owned by the app's root component with a matching teardown — the same shape
 * as the `session-expired` listener in `SessionExpiredModal.vue`, which is the
 * established convention for this class of cross-cutting RPC event. The
 * alternative it replaces was a byte-identical block at module scope in both
 * routers, registered as a bare import side effect with no way to remove it.
 *
 * Also watches the store's own flag. Without that watch the redirect only
 * fired on a navigation (the router guard) or after the server had already
 * refused a call — so a background permissions refresh that DISCOVERED the
 * flag (the periodic/visibility refresh, e.g. an administrator reset landing
 * mid-session) left the current screen sitting there until the user happened
 * to navigate or trip a blocked request.
 *
 * @param {import('vue-router').Router} router
 * @param {object} [options]
 * @param {string} [options.routeName] Route to send the user to.
 * @param {import('vue').Ref<boolean>|(() => boolean)} [options.mustChangePassword]
 *        Reactive source for the store's gate flag. Omit to rely on the event
 *        and the router guard alone.
 */
export function usePasswordChangeRedirect(router, options = {}) {
  const { routeName = 'ForcedPasswordChange', mustChangePassword } = options

  function redirectUnlessAlreadyThere() {
    if (router.currentRoute.value.name !== routeName) {
      router.push({ name: routeName })
    }
  }

  onMounted(() => {
    window.addEventListener(PASSWORD_CHANGE_REQUIRED_EVENT, redirectUnlessAlreadyThere)
  })

  onUnmounted(() => {
    window.removeEventListener(PASSWORD_CHANGE_REQUIRED_EVENT, redirectUnlessAlreadyThere)
  })

  if (mustChangePassword) {
    watch(
      mustChangePassword,
      (gated) => {
        if (gated) redirectUnlessAlreadyThere()
      },
      { immediate: true },
    )
  }
}
